import Colors from '../../../utils/Colors.js'
import {Point, validPosition} from '../../../src/components/map/geometry.js'

// What the courier's route map shows, worked out from the route without Leaflet so it can be unit tested: the
// markers of the open stops, start and finish. Positions that are missing or incomplete are left out (the map
// threw on them).

export interface Position {
    lat?: number
    lng?: number
}

export interface RouteMapAddress {
    id?: string
    orderId?: string
    index?: number
    type?: string
    position?: Position
    stopNumber?: number | string
    hasArrived?: boolean
    isExpected?: boolean
    isRetour?: boolean
    bundleId?: string
}

export interface RouteMapRoute {
    started?: boolean
    addresses?: RouteMapAddress[]
    startPoint?: {position?: Position}
    endPoint?: {position?: Position}
    geometry?: object
    orders?: Record<string, {addresses?: RouteMapAddress[]}>
    todoItems?: Array<{id: string}>
}

export type LatLng = Point

/** A stop of an order on the map, the one whose info is open */
export interface StopKey {
    orderId: string
    addressIndex: number
}

export interface RouteMarker {
    position: LatLng
    label: string | number
    color?: string
    /** Fill opacity, expected stops are half transparent */
    opacity: number
    /** The finish, drawn with a checker pattern */
    checkered?: boolean
    /** Set on the markers of a stop, which open its info */
    stop?: StopKey
}

/** A stop still to go to: not arrived at, and once the route is started not one that is only expected */
export function isOpenStop(route: RouteMapRoute, address: RouteMapAddress): boolean {
    return !address.hasArrived && !(route.started && address.isExpected)
}

// the first open stop, or the end point once all stops are done
export function getNextStopPosition(route?: RouteMapRoute | null): Position | undefined {
    const nextStop = route?.addresses?.find((address) => isOpenStop(route, address))

    if (nextStop) {
        return nextStop.position
    }

    const endPosition = route?.endPoint?.position

    if (typeof endPosition?.lat === 'number' && typeof endPosition?.lng === 'number') {
        return endPosition
    }
}

/**
 * The markers of the route in drawing order, the last one on top: the open stops (a bundle at its first stop; once
 * the route is started without the expected ones), then the finish and, before the route is started, the
 * start. A route without addresses (not loaded yet) has no markers.
 */
export function buildRouteMarkers(route: RouteMapRoute | null | undefined): RouteMarker[] {
    if (!route?.addresses) {
        return []
    }

    const markers: RouteMarker[] = []
    const add = (position: Position | undefined | null, marker: Omit<RouteMarker, 'position'>) => {
        const latLng = validPosition(position)

        if (latLng) {
            markers.push({position: latLng, ...marker})
        }
    }

    if (!route.started && route.startPoint) {
        add(route.startPoint.position, {color: Colors.successBright, label: '', opacity: 1})
    }

    if (route.endPoint) {
        add(route.endPoint.position, {checkered: true, label: '', opacity: 1})
    }

    for (const address of route.addresses) {
        // A bundle is shown as one marker, at its first stop
        if (!isOpenStop(route, address) || (address.bundleId && address.bundleId !== `bundle ${address.id}`)) {
            continue
        }

        add(address.position, {
            color: address.isRetour ? Colors.retour : Colors[address.type],
            opacity: address.isExpected ? 0.5 : 1,
            label: address.stopNumber ? address.stopNumber : (address.isRetour ? 'R' : address.type === 'pickup' ? 'P' : 'D'),
            stop: {orderId: address.orderId, addressIndex: address.index}
        })
    }

    return markers.reverse()
}

export function isSameStop(a?: StopKey | null, b?: StopKey | null) {
    return !!a && !!b && a.orderId === b.orderId && a.addressIndex === b.addressIndex
}

/** The order, address and bundle of the stop whose info is open, from the current route */
export function getStopInfo(route: RouteMapRoute | null | undefined, stop: StopKey | null) {
    if (!stop) {
        return null
    }

    const order = route?.orders?.[stop.orderId]
    const address = order?.addresses?.[stop.addressIndex]

    if (!order || !address) {
        return null
    }

    const bundle = (address.bundleId && route.todoItems?.find((item) => item.id === address.bundleId)) || null

    return {order, address, bundle}
}
